import { h, Component } from 'preact';
import Store from 'store/store';
import s from 'components/bolt/videos/videos.sss';
import VideoPlayer from 'components/bolt/videos/player';
import VideosPlaylist from 'components/bolt/videos/playlist/playlist';
import { accessNested, sortObjectByOrder, toQueryString, joinClasses } from 'utils/utils';
import { arrayWithUniqueKeys } from 'utils/bolt';
import slugify from 'utils/slugify';
import parseQueryString from 'utils/parseQueryString';
import { getPlaylistData } from 'services/boltYoutube';

export default class Videos extends Component {

	constructor(props) {
		super(props);
		this.state = {
			videos: [],
			loaded: false
		};
		this.changeActiveVideo = this.changeActiveVideo.bind(this);
		this.onEndedHandler = this.onEndedHandler.bind(this);
		this.onStartHandler = this.onStartHandler.bind(this);

		this.preSelectedKey = accessNested(props, 'store.videos.preSelected');
		this.handleEscapeKey = this.handleEscapeKey.bind(this);
	}

	componentDidMount() {
		const { store } = this.props;
		const videoDatalist = accessNested(store, `appData.pages.${store.videos?.overlayOpen}.data.list`, {});
		const videoDataArray = sortObjectByOrder(videoDatalist, (value, key) => ({ ...value, key: slugify(key) }));
		if (!this.state.videos.length) {
			this.replacePlaylistWithVideos(videoDataArray);
		}
		document.addEventListener('keydown', this.handleEscapeKey, false);
	}

	componentWillUnmount() {
		Store.get().videos.set({ activeVideo: '' });
		document.removeEventListener('keydown', this.handleEscapeKey, false);
	}

	handleEscapeKey(e) {
		if (e.keyCode === 27) {
			const { store } = this.props;
			const videoPath = accessNested(this.props, `store.appData.pages.${store.videos?.overlayOpen}.path`);
			const videoIsRootDomain = videoPath === '/';
			if (!videoIsRootDomain) Store.emit('CLOSE_VIDEO_OVERLAY');
		}
	}

	/**
	* Returns the gallery data array without playlist objects.
	*
	* @param {Array.<object>} array
	* @returns {Array.<object>} array of objects
	**/
	removeYoutubePlaylists(array) {
		return array.filter((item) => !item['youtubePlaylistId'] && item );
	}

	findGalleryItemForQueryStringOrHash(videos, queryString = parseQueryString(), hash = location.hash.slice(1)) {
		let checkAgainst = queryString?.v || hash;

		return videos.find((item) => [item.vimeoId, item.youtubeId, item.key].includes(checkAgainst));
	}

	async getVideosFromPlaylist(vid) {
		const playlistId = vid.youtubePlaylistId;
		const noCookies = vid.noCookies;
		let data = await getPlaylistData(playlistId);
		return data.items.filter(item => item.snippet.thumbnails.medium).sort((a, b) => this.sortPlaylist(a, b, vid.playlistOrder)).map(item => ({
			name: item.snippet.title,
			thumbSrc: item.snippet.thumbnails.medium.url,
			youtubeId: item.snippet.resourceId.videoId,
			key: item.id,
			noCookies
		}));
	}

	sortPlaylist(a, b, order) {
		if (!order) {
			return 0;
		}
		const playlistOrder = order.replace(/\s/g, '').split(',').reverse();

		return playlistOrder.indexOf(b.snippet.resourceId.videoId) - playlistOrder.indexOf(a.snippet.resourceId.videoId);
	}

	async replacePlaylistWithVideos(videos) {
		let newVideos = [];
		try {
			let promises = videos.map(vid => vid?.youtubePlaylistId ? this.getVideosFromPlaylist(vid) : vid);
			let data = await Promise.all(promises);
			const concatArray = [].concat(...data);
			newVideos = arrayWithUniqueKeys(concatArray);
		} catch (e) {
			newVideos = this.removeYoutubePlaylists(videos);
		}
		this.setState({ videos: newVideos, loaded: true });
	}

	changeActiveVideo(video) {
		Store.emit('CHANGE_ACTIVE_VIDEO', video);
	}

	getNextVideo(activeVideo, videos) {
		if (!activeVideo || !videos?.length) {
			return;
		}

		const activeVidIndex = videos.findIndex((vid) => vid.key === activeVideo.key);
		return videos[activeVidIndex + 1];
	}

	getLandingVideo() {
		// Because getting videos can take time, run only when data exists.
		if (this.state.loaded) {
			const videos = this.state.videos;
			const queryString = parseQueryString();
			const queriedVideo = this.findGalleryItemForQueryStringOrHash(videos, queryString);

			if (queriedVideo) {
				Store.get().videos.set({ activeVideo: queriedVideo });
			}
			// We handled the video, remove it from the url
			// (I don't think it's bad to keep it in the url but it is the way it's been implemented)
			delete queryString.v;
			let newQS = toQueryString(queryString);
			if (newQS !== location.search) {
				history.replaceState?.({}, '', '.' + newQS);
			}

			let preSelected;
			if (this.preSelectedKey) {
				let lower = this.preSelectedKey.toLowerCase();
				let keysPriority = ['key', 'youtubeId', 'vimeoId'];
				let compare = (a, b, v) => a === v ? -1 : b === v ? 1 : 0;
				preSelected = videos.slice().sort((a, b) => {
					return keysPriority.reduce((o, k) => (
						o ||
					compare(a[k], b[k], this.preSelectedKey) ||
					compare(a[k] && a[k].toLowerCase(), b[k] && b[k].toLowerCase(), lower)
					), 0);
				}).shift();
			}

			return queriedVideo || preSelected || videos[0];
		}
	}

	onEndedHandler() {
		const { store } = this.props;
		const activeVideo = accessNested(this.props, 'store.videos.activeVideo', this.state.videos[0]);
		const videoPath = accessNested(this.props, `store.appData.pages.${store.videos?.overlayOpen}.path`);
		const videoIsRootDomain = videoPath === '/';
		const nextVideo = this.getNextVideo(activeVideo, this.state.videos);
		Store.emit('VIDEO_ENDED', activeVideo);
		if (nextVideo) {
			this.changeActiveVideo(nextVideo);
		} else if (!videoIsRootDomain) {
			Store.emit('CLOSE_VIDEO_OVERLAY');
		}
	}

	onStartHandler() {
		const activeVideo = accessNested(this.props, 'store.videos.activeVideo') || this.getLandingVideo();
		Store.emit('VIDEO_STARTED', activeVideo, this.$player);
	}

	render(props, state) {
		const activeVideo = accessNested(props, 'store.videos.activeVideo') || this.getLandingVideo();
		let { pushDown, height: cookieHeight, cookieBannerOpen } = accessNested(props, 'store.cookies', {});
		const evidonMode = accessNested(props, 'store.evidon.bannerOpen');
		const sonyHeader = accessNested(props, 'store.appData.options.sonyFeatures.sonyHeader', false);
		const cookieMode = evidonMode || (pushDown && cookieBannerOpen);
		let height = '';
		let transform = '';
		let paddingTop = (sonyHeader ? 34 : 0) + 'px';

		if (evidonMode) {
			cookieHeight = accessNested(props, 'store.evidon.height');
		}

		if (cookieMode) {
			height = `calc(100% - ${cookieHeight}px)`;
			transform = `translateY(${cookieHeight}px)`;
		}

		let hidePlaylist = state.videos.length < 2;

		const ageGateEnabled = props.store.appData.options.ageGate;
		let ageGateProtected = false;
		if (ageGateEnabled) {
			const activeVideosPage = props.store.videos?.overlayOpen;
			const ageGateOnVideoPage = (ageGateEnabled && !ageGateEnabled.onlyOnPages) || ageGateEnabled.onlyOnPages.split(',').map(e => e.trim()).includes(activeVideosPage);
			const ageGateOpen = props.store?.ageGateOpen !== false;
			ageGateProtected = ageGateOnVideoPage && ageGateOpen;
		}

		return (
			<div class={joinClasses(s.container, props.store.videos?.overlayOpen)} style={{ ...props.style, height, transform, paddingTop }}>
				<VideoPlayer
					ref={e => this.$player = e}
					activeVideo={activeVideo}
					onEnded={this.onEndedHandler}
					onStart={this.onStartHandler}
					hiddenPlaylist={hidePlaylist}
					ageGateProtected={ageGateProtected}
				/>
				<VideosPlaylist
					videos={state.videos}
					activeVideo={activeVideo}
					changeActiveVideo={this.changeActiveVideo}
					hidden={hidePlaylist}
				/>
			</div>
		);
	}
}
